import { offsetPathByPixels } from "./offsetPath";

export type LensTrip = {
  tripKey: string;
  color: string;
  rawPath: { lat: number; lng: number }[];
  dayLabel: string;
  tripNumber: number;
};

const LENS_DIAMETER = 180;
const LENS_ZOOM_BOOST = 3;
const LENS_NUDGE_PIXELS = 10;


// lens instead of a fixed page corner.
export const createClusterLensOverlay = ({
  google,
  mainMap,
  position,
  trips,
  showPanel = true,
  onTripClick,
  onClose,
}: {
  google: any;
  mainMap: any;
  position: { lat: number; lng: number };
  trips: LensTrip[];
  // The lens has its own list of what is under it, which is right when it
  // opens on its own. The multi-vehicle map opens it beside the cluster
  // popup, which already names the trips, so there it is the magnifier
  // alone: the routes pulled far enough apart to tell which is which.
  showPanel?: boolean;
  onTripClick: (tripKey: string) => void;
  onClose: () => void;
}) => {
  class ClusterLensOverlay extends google.maps.OverlayView {
    container: HTMLDivElement | null = null;
    lensMap: any = null;

    onAdd() {
      this.container = document.createElement("div");
      this.container.style.cssText = `
        position: absolute;
        display: flex;
        align-items: flex-start;
        gap: 10px;
        transform: translate(-${LENS_DIAMETER / 2}px, -${LENS_DIAMETER / 2}px);
      `;

      const lensWrapper = document.createElement("div");
      lensWrapper.style.cssText = `
        width: ${LENS_DIAMETER}px;
        height: ${LENS_DIAMETER}px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
        box-shadow: 0 4px 20px rgba(0,0,0,0.4), 0 0 0 4px white, 0 0 0 5px rgba(0,0,0,0.15);
        background: #eee;
      `;
      const lensMapDiv = document.createElement("div");
      lensMapDiv.style.cssText = "width: 100%; height: 100%;";
      lensWrapper.appendChild(lensMapDiv);

      const panel = document.createElement("div");
      panel.style.cssText = `
        background: white;
        border-radius: 10px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.25);
        padding: 10px 12px;
        font-size: 12px;
        min-width: 170px;
        max-width: 230px;
        font-family: inherit;
      `;

      const header = document.createElement("div");
      header.style.cssText =
        "display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:6px; font-weight:600;";
      const headerLabel = document.createElement("span");
      headerLabel.textContent = `${trips.length} trips overlap here`;
      const closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.textContent = "×";
      closeBtn.style.cssText =
        "border:none; background:none; cursor:pointer; font-size:16px; line-height:1; color:#9CA3AF; padding:0;";
      closeBtn.addEventListener("click", onClose);
      header.appendChild(headerLabel);
      header.appendChild(closeBtn);
      panel.appendChild(header);

      const list = document.createElement("div");
      list.style.cssText = "display:flex; flex-direction:column; gap:2px;";
      trips.forEach((trip) => {
        const row = document.createElement("button");
        row.type = "button";
        row.style.cssText = `
          display:flex; align-items:center; gap:6px;
          border:none; background:transparent; cursor:pointer;
          padding:5px 6px; border-radius:6px; text-align:left; font-size:12px;
          width: 100%;
        `;
        row.addEventListener("mouseenter", () => {
          row.style.background = "#EEF3FB";
        });
        row.addEventListener("mouseleave", () => {
          row.style.background = "transparent";
        });
        row.innerHTML = `
          <span style="width:10px;height:10px;border-radius:50%;background:${trip.color};flex-shrink:0;"></span>
          <span>${trip.dayLabel} &middot; Trip ${trip.tripNumber}</span>
        `;
        row.addEventListener("click", () => onTripClick(trip.tripKey));
        list.appendChild(row);
      });
      panel.appendChild(list);

      this.container.appendChild(lensWrapper);
      if (showPanel) this.container.appendChild(panel);
      this.getPanes().floatPane.appendChild(this.container);

      // the actual zoomed-in map instance that makes this a real magnifier,
      // not an illustration
      this.lensMap = new google.maps.Map(lensMapDiv, {
        center: position,
        zoom: Math.min(21, mainMap.getZoom() + LENS_ZOOM_BOOST),
        mapTypeId: mainMap.getMapTypeId?.() ?? "roadmap",
        disableDefaultUI: true,
        draggable: false,
        scrollwheel: false,
        keyboardShortcuts: false,
        clickableIcons: false,
        gestureHandling: "none",
      });

      google.maps.event.addListenerOnce(this.lensMap, "idle", () => {
        this.drawLensRoutes();
      });
    }

    drawLensRoutes() {
      if (!this.lensMap) return;
      const zoom = this.lensMap.getZoom();
      const center = (trips.length - 1) / 2;
      trips.forEach((trip, index) => {
        const magnitude = (index - center) * LENS_NUDGE_PIXELS;
        const nudgedPath = offsetPathByPixels(
          this.lensMap,
          trip.rawPath,
          magnitude,
          magnitude,
          zoom
        );
        new google.maps.Polyline({
          path: nudgedPath,
          map: this.lensMap,
          strokeColor: trip.color,
          strokeOpacity: 0.95,
          strokeWeight: 4,
        });
      });
    }

    draw() {
      const projection = this.getProjection();
      if (!projection || !this.container) return;
      const point = projection.fromLatLngToDivPixel(
        new google.maps.LatLng(position.lat, position.lng)
      );
      if (point) {
        this.container.style.left = `${point.x}px`;
        this.container.style.top = `${point.y}px`;
      }
    }

    onRemove() {
      if (this.container?.parentNode) {
        this.container.parentNode.removeChild(this.container);
      }
      this.container = null;
      this.lensMap = null;
    }
  }

  return new ClusterLensOverlay();
};
